import * as React from "react";
import { createElement, init } from "@airwallex/components-sdk";
import { getSCAEmbeddedInitApi } from "../../api/api";
import { drupalSettings, getScaInitUid } from "../../utils/config";
import { parsePhone } from "../../utils/utils";

export function ScaVerify({
  key,
  onReady = () => {},
  onVerificationSucceed = (token) => { console.log(token) },
  onVerificationFailed = () => {},
  onError = () => {},
  onCancel = () => {},
  onScaSetupSucceed = () => {},
  scaSessionCode,
}) {
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let scaElement = null;
    let isUnmounted = false;

    async function initializeAirwallex() {
      try {
        // -------------------------
        // 1. Load Init Config
        // -------------------------
        const { data, env = "demo" } = await getSCAEmbeddedInitApi({
          uid: getScaInitUid(),
          yacht_id: drupalSettings?.user?.yacht_id,
        });
        const { authCode, clientId, codeVerifier } = data || {};

        if (!authCode || !clientId || !codeVerifier) {
          throw new Error("Missing Airwallex init params");
        }

        console.log("SCA Embedded Init Response:", data);

        // -------------------------
        // 2. Initialise Airwallex
        // -------------------------
        await init({
          locale: "en",
          env,
          authCode,
          clientId,
          codeVerifier,
        });

        // -------------------------
        // 3. Prepare prefilled phone
        // -------------------------
        const phoneRaw = drupalSettings?.user?.mobile_number ?? "";
        const parsedPhone = parsePhone(phoneRaw);

        let scaOptions = {
          userEmail: drupalSettings?.user?.mail || undefined,
        };

        if (scaSessionCode) {
          scaOptions.scaSessionCode = scaSessionCode
        }

        if (parsedPhone.valid) {
          scaOptions.prefilledMobileInfo = {
            countryCode: parsedPhone.countryCode,
            nationalNumber: parsedPhone.number,
          };
        }

        console.log("Initializing SCA Verify:", scaOptions);

        // -------------------------
        // 4. Create element
        // -------------------------
        scaElement = await createElement("scaVerify", scaOptions);

        if (isUnmounted || !scaElement) return;

        scaElement.mount("#airwallex-sca-embedded-component");

        // -------------------------
        // 5. Bind events
        // -------------------------
        scaElement.on("ready", () => {
          setLoading(false);
          onReady();
        });

        scaElement.on("verificationSucceed", ({ token }) => {
          console.log("Verification Succeed");
          onVerificationSucceed({ token });
        });

        scaElement.on("verificationFailed", ({ reason }) => {
          console.log("Verification Failed");
          onVerificationFailed({ reason });
        });

        scaElement.on("error", (error) => {
          console.error("Airwallex SCA Error:", error);
          onError(error);
        });

        scaElement.on("cancel", (event) => {
          console.log("SCA Cancelled");
          onCancel(event);
        });

        scaElement.on("scaSetupSucceed", ({ mobileInfo }) => {
          console.log("SCA Setup Succeed", mobileInfo)
          onScaSetupSucceed({ mobileInfo })
        });
      } catch (err) {
        console.error("Error initializing Airwallex:", err);
        onError(err);
      }
    }

    initializeAirwallex();

    // -------------------------
    // 6. Cleanup on unmount
    // -------------------------
    return () => {
      isUnmounted = true;
      try {
        scaElement?.unmount?.();
      } catch (e) {
        console.warn("Failed to unmount SCA element:", e);
      }
    };
  }, []);

  return (
    <>
      {loading && (
        <div className="ajax-progress ajax-progress-throbber">
          <div className="throbber"></div>
        </div>
      )}

      <div key={key} id="airwallex-sca-embedded-component"></div>
    </>
  );
}
